import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/PageHeader';
import { ownerPresentation } from '@/api/business-owner';
import { useAuth } from '@/context/AuthContext';

const PARTS = ['roots', 'languages'] as const;

/**
 * "Meet the owner", by hand: the primary owner turns it on or off, picks the
 * parts of their own Parichay, and sees exactly what a stranger reads on the
 * business page. Off by default; a manager is refused.
 */
export function OwnerPresentationPanel({ providerId }: { providerId: string }) {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['owner-presentation', user?.id, providerId],
    queryFn: () => ownerPresentation.get(providerId),
    retry: false,
  });
  const [show, setShow] = useState(false);
  const [parts, setParts] = useState<string[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (query.data) { setShow(query.data.data.show); setParts(query.data.data.parts); }
  }, [query.data]);

  async function save() {
    setError('');
    try {
      await ownerPresentation.set(providerId, show, parts);
      await query.refetch();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The request failed.');
    }
  }

  const preview = query.data?.data.owner ?? null;
  return <section className="border-t border-gray-200 pt-4 mt-4 space-y-2" aria-label="Meet the owner">
    <h3 className="font-semibold text-sm">Meet the owner</h3>
    {query.error && <p role="alert" className="text-sm text-red-700">{query.error.message}</p>}
    <label className="flex gap-2 text-sm"><input type="checkbox" checked={show} onChange={(event) => setShow(event.target.checked)} />Show me on this business's page</label>
    <div className="flex gap-3 text-sm">
      {PARTS.map((part) => <label key={part} className="flex gap-1">
        <input type="checkbox" checked={parts.includes(part)} onChange={(event) =>
          setParts((old) => event.target.checked ? [...old, part] : old.filter((p) => p !== part))} />{part}
      </label>)}
    </div>
    <Button size="sm" onClick={() => void save()}>Save</Button>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <p className="text-sm text-gray-600">
      {preview
        ? <>A stranger sees: {preview.name}{preview.roots ? ` · roots in ${preview.roots.map((r) => r.place ?? r.name).join(', ')}` : ''}{preview.languages ? ` · speaks ${preview.languages.map((l) => l.name).join(', ')}` : ''}</>
        : 'A stranger sees nobody behind this business.'}
    </p>
  </section>;
}
